/**
 * Pop-out modal dialog for uploading a new clinical source.
 *
 * Provides a clean modal interface for uploading pasted source text or text files
 * (.txt, .md, text/plain) without cluttering the sources rail list.
 *
 * Enforces disable-until-valid semantics: both title and body are required before
 * submission, and actions are locked while ingestion is actively in progress.
 */
import React, { useEffect, useRef, useState } from "react";
import { fileResultToText } from "./fileText";
import { IngestProgress } from "./IngestProgress";

export interface AddSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (title: string, body: string) => void | Promise<void>;
  ingesting?: boolean;
}

export const AddSourceModal: React.FC<AddSourceModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  ingesting = false,
}) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus title input on mount when opened
  useEffect(() => {
    if (isOpen) {
      // Focus after mount tick
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Window keydown listener for Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !ingesting) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, ingesting, onClose]);

  if (!isOpen) {
    return null;
  }

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const canSubmit = trimmedTitle !== "" && trimmedBody !== "" && !ingesting;
  const showIncompleteHint =
    !ingesting && (trimmedTitle === "" || trimmedBody === "");

  const handleBackdropClick = (): void => {
    if (!ingesting) {
      onClose();
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = fileResultToText(reader.result);
      if (text.trim() === "") {
        setFileNotice(
          "That file looks empty or isn't readable as text — paste the text instead.",
        );
        return;
      }
      setFileNotice(null);
      setBody(text);
      if (title.trim() === "") {
        setTitle(file.name);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const submit = async (): Promise<void> => {
    try {
      await onUpload(trimmedTitle, trimmedBody);
      setTitle("");
      setBody("");
      setFileNotice(null);
      onClose();
    } catch {
      // Upon error/rejection in onUpload, do not close modal, allowing clinician retry.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add source"
      data-testid="add-source-modal"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        className="flex flex-col w-full max-w-lg rounded-xl bg-white p-5 shadow-xl space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Add source</h3>
          <button
            type="button"
            data-testid="add-source-modal-close"
            aria-label="Close"
            disabled={ingesting}
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-base leading-none font-bold p-1 rounded"
          >
            ×
          </button>
        </div>

        <input
          ref={titleInputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Source title (e.g. intake note)"
          aria-label="Source title"
          data-testid="source-title-input"
          className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setFileNotice(null);
          }}
          placeholder="Paste transcript text…"
          aria-label="Source body"
          data-testid="source-body-input"
          className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm min-h-[140px] font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        <div>
          <input
            type="file"
            id="source-file-upload"
            accept=".txt,.md,text/plain"
            aria-label="Upload transcript file"
            onChange={handleFile}
            className="sr-only"
          />
          <label
            htmlFor="source-file-upload"
            className="inline-block w-full px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded text-sm text-center cursor-pointer hover:bg-slate-50 transition-colors"
          >
            Choose file
          </label>
        </div>

        {ingesting ? (
          <IngestProgress
            testId="ingesting-indicator"
            className="justify-center"
          />
        ) : null}

        {fileNotice ? (
          <p
            role="alert"
            data-testid="upload-source-error"
            className="text-xs text-rose-700"
          >
            {fileNotice}
          </p>
        ) : null}

        {showIncompleteHint ? (
          <p
            id="upload-source-hint"
            data-testid="upload-source-hint"
            className="text-xs text-slate-500"
          >
            A title and source text are required to upload.
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            data-testid="add-source-cancel-button"
            disabled={ingesting}
            onClick={onClose}
            className="px-3 py-1.5 border border-slate-200 text-slate-700 rounded text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            aria-busy={ingesting}
            aria-describedby={
              showIncompleteHint ? "upload-source-hint" : undefined
            }
            data-testid="upload-source-button"
            className="px-4 py-1.5 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Upload source
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSourceModal;
